import { useEffect, useState } from 'react';
import type {
  Baby,
  BlockBabyMeal,
  BlockMotherFitness,
  BlockMotherMeal,
  BlockTip,
  DailyContentBlocks,
} from '@/types';
import { api } from '@/lib/api-client';
import { isGerman } from '@/i18n/locale';
import { DailyBriefingCard } from '@/components/today/DailyBriefingCard';
import { Icon } from '@/components/ui/icons';

// DailyContentCards — Phase 4 Pro surface on /today.
//
// Owns the GET /api/daily-content fetch and branches on response.kind:
//   - 'free_tier'      → render the existing single-block <DailyBriefingCard>
//   - 'pro'            → render 4 stacked cards (tip / baby meal / mother
//                        meal / mother fitness). null slots show "generating…"
//
// Loading state shows 4 skeleton cards (per locked decision #5: blocking
// generation, all 4 swap in together; no SSE / progressive fill in V1).
// Network errors silently fall through to the single-block UI — /today
// never breaks.

interface Props {
  baby: Baby;
}

type ViewState =
  | { kind: 'loading' }
  | { kind: 'pro'; blocks: DailyContentBlocks }
  | { kind: 'free_tier' };

export function DailyContentCards({ baby }: Props) {
  const de = isGerman();
  const [view, setView] = useState<ViewState>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setView({ kind: 'loading' });
    api.dailyContent
      .get(baby.id)
      .then((res) => {
        if (cancelled) return;
        if (res.kind === 'pro') {
          setView({ kind: 'pro', blocks: res.blocks });
        } else {
          setView({ kind: 'free_tier' });
        }
      })
      .catch(() => {
        if (cancelled) return;
        // Network or unauth — degrade silently to single-block UI.
        setView({ kind: 'free_tier' });
      });
    return () => {
      cancelled = true;
    };
  }, [baby.id]);

  if (view.kind === 'free_tier') {
    return <DailyBriefingCard baby={baby} />;
  }

  if (view.kind === 'loading') {
    return (
      <div className="space-y-3">
        <SkeletonCard tall />
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }

  const { tip, baby_meal, mother_meal, mother_fitness } = view.blocks;
  const pending = de ? 'wird erstellt…' : 'generating…';

  return (
    <div className="space-y-3">
      <TipCard tip={tip} />
      <BabyMealCard meal={baby_meal} pendingLabel={pending} babyName={baby.name} />
      <MotherMealCard meal={mother_meal} pendingLabel={pending} />
      <MotherFitnessCard fitness={mother_fitness} pendingLabel={pending} />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Cards
// ────────────────────────────────────────────────────────────────────

function TipCard({ tip }: { tip: BlockTip }) {
  const de = isGerman();
  return (
    <section className="rounded-2xl bg-gradient-to-br from-primary/[0.06] via-cream to-accent/[0.08] border border-primary-100 p-5">
      <div className="flex items-center gap-1.5 text-xs font-medium text-primary-700 uppercase tracking-wide mb-1.5">
        <Icon.Lightbulb size={14} />
        <span>{de ? 'Tipp des Tages' : 'Tip of the day'}</span>
      </div>
      <h3 className="font-display font-semibold text-slate leading-snug">{tip.title}</h3>
      <p className="text-sm text-slate mt-1.5 leading-relaxed">{tip.body}</p>
    </section>
  );
}

function BabyMealCard({
  meal,
  pendingLabel,
  babyName,
}: {
  meal: BlockBabyMeal | null;
  pendingLabel: string;
  babyName: string;
}) {
  const de = isGerman();
  const heading = de ? `Mahlzeit für ${babyName}` : `Meal for ${babyName}`;
  if (!meal) return <PendingCard heading={heading} icon={<Icon.Bowl size={14} />} pendingLabel={pendingLabel} />;
  return (
    <section className="card p-5">
      <CardHeader heading={heading} icon={<Icon.Bowl size={14} />} tone="primary" />
      <h3 className="font-display font-semibold text-slate leading-snug mt-1">{meal.title}</h3>
      <p className="text-xs text-slate-muted mt-1">
        {meal.prep_minutes > 0
          ? `${meal.prep_minutes} min · ${meal.ingredients.length} ${de ? 'Zutaten' : 'ingredients'}`
          : (de ? `${meal.ingredients.length} Zutaten` : `${meal.ingredients.length} ingredients`)}
      </p>
      <IngredientList items={meal.ingredients} />
      <p className="text-xs text-slate-muted leading-relaxed mt-3">{meal.why_for_this_age}</p>
    </section>
  );
}

function MotherMealCard({
  meal,
  pendingLabel,
}: {
  meal: BlockMotherMeal | null;
  pendingLabel: string;
}) {
  const de = isGerman();
  const heading = de ? 'Für dich' : 'For you';
  if (!meal) return <PendingCard heading={heading} icon={<Icon.Utensils size={14} />} pendingLabel={pendingLabel} />;
  return (
    <section className="card p-5">
      <CardHeader heading={heading} icon={<Icon.Utensils size={14} />} tone="accent" />
      <h3 className="font-display font-semibold text-slate leading-snug mt-1">{meal.title}</h3>
      <p className="text-xs text-slate-muted mt-1">
        {meal.prep_minutes > 0
          ? `${meal.prep_minutes} min · ${meal.ingredients.length} ${de ? 'Zutaten' : 'ingredients'}`
          : (de ? `${meal.ingredients.length} Zutaten` : `${meal.ingredients.length} ingredients`)}
      </p>
      <IngredientList items={meal.ingredients} />
    </section>
  );
}

const INTENSITY_LABEL_EN: Record<BlockMotherFitness['intensity'], string> = {
  gentle: 'gentle',
  moderate: 'moderate',
  active: 'active',
};
const INTENSITY_LABEL_DE: Record<BlockMotherFitness['intensity'], string> = {
  gentle: 'sanft',
  moderate: 'moderat',
  active: 'aktiv',
};

function MotherFitnessCard({
  fitness,
  pendingLabel,
}: {
  fitness: BlockMotherFitness | null;
  pendingLabel: string;
}) {
  const de = isGerman();
  const heading = de ? 'Bewegung heute' : 'Movement today';
  if (!fitness) return <PendingCard heading={heading} icon={<Icon.HeartPulse size={14} />} pendingLabel={pendingLabel} />;
  const intensityLabel = (de ? INTENSITY_LABEL_DE : INTENSITY_LABEL_EN)[fitness.intensity];
  return (
    <section className="card p-5">
      <CardHeader heading={heading} icon={<Icon.HeartPulse size={14} />} tone="primary" />
      <h3 className="font-display font-semibold text-slate leading-snug mt-1">{fitness.title}</h3>
      <p className="text-xs text-slate-muted mt-1">
        {fitness.duration_minutes} min · {intensityLabel}
      </p>
      <ol className="mt-3 space-y-1.5">
        {fitness.steps.map((step, i) => (
          <li key={i} className="text-sm text-slate leading-relaxed flex gap-2">
            <span
              className="shrink-0 w-5 h-5 rounded-full bg-primary-50 text-primary-700 text-[11px] font-semibold flex items-center justify-center mt-0.5"
              aria-hidden
            >
              {i + 1}
            </span>
            <span className="flex-1">{step}</span>
          </li>
        ))}
      </ol>
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────
// Small reused subcomponents
// ────────────────────────────────────────────────────────────────────

function CardHeader({
  heading,
  icon,
  tone,
}: {
  heading: string;
  icon: React.ReactNode;
  tone: 'primary' | 'accent';
}) {
  const toneClass =
    tone === 'primary' ? 'text-primary-700' : 'text-accent-600';
  return (
    <div className={`flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide ${toneClass}`}>
      {icon}
      <span>{heading}</span>
    </div>
  );
}

function IngredientList({ items }: { items: string[] }) {
  return (
    <ul className="mt-2 space-y-1">
      {items.map((ing, i) => (
        <li key={i} className="text-sm text-slate leading-relaxed flex items-baseline gap-2">
          <span className="text-primary-300 mt-0.5" aria-hidden>·</span>
          <span className="flex-1">{ing}</span>
        </li>
      ))}
    </ul>
  );
}

function PendingCard({
  heading,
  icon,
  pendingLabel,
}: {
  heading: string;
  icon: React.ReactNode;
  pendingLabel: string;
}) {
  return (
    <section className="card p-5" aria-busy>
      <CardHeader heading={heading} icon={icon} tone="primary" />
      <div className="mt-3 space-y-2 animate-pulse" aria-hidden>
        <div className="h-4 w-2/3 bg-primary-100 rounded" />
        <div className="h-3 w-full bg-primary-50 rounded" />
        <div className="h-3 w-5/6 bg-primary-50 rounded" />
      </div>
      <p className="text-xs text-slate-muted mt-2">{pendingLabel}</p>
    </section>
  );
}

function SkeletonCard({ tall }: { tall?: boolean }) {
  return (
    <section
      className={
        'rounded-2xl bg-white/60 border border-primary-50 animate-pulse ' +
        (tall ? 'h-40' : 'h-28')
      }
      aria-hidden
    />
  );
}
